import type { ProfileSummary, ToolResultMessage } from "../types";
import { Table } from "./Table";
import { Document } from "./Document";
import { Checklist } from "./Checklist";
import { Form } from "./Form";
import { Tips } from "./Tips";
import { Draft } from "./Draft";
import { Schedule } from "./Schedule";
import { PageEditor } from "./PageEditor";
import { Plan } from "./Plan";
import { Answer } from "./Answer";
import { Tree } from "./Tree";
import { Files } from "./Files";
import { Chart } from "./Chart";
import { Research } from "./Research";
import { ReportDocument } from "./ReportBadge";

/**
 * The component renderer registry.
 *
 * An agent calls a tool; if the profile declared that tool name as a UI component, the chat
 * stream renders one of these in place of the usual collapsed tool-call panel. A component's
 * arguments are references, not data — the renderer fetches whatever they point at through the
 * workspace file route (`GET /api/sessions/:id/workspace/<path>`), rather than trusting the model
 * to have retyped it correctly. Form is a deliberate, documented exception to that — see its own
 * doc comment.
 *
 * This is the one place `ComponentProps`/`ComponentRenderer` are defined. Table, Document,
 * Checklist and Form all import the type from here and register into the same `RENDERERS` map;
 * every renderer must match the same props shape.
 */
export interface ComponentProps {
	sessionId: string;
	/** The tool call's id — interactive renderers post back to it. */
	callId: string;
	/** ToolCallContent.arguments, verbatim. Renderers validate what they need. */
	args: Record<string, unknown>;
	/** Present once the call has returned. Its presence is what makes an
	 *  interactive renderer read-only after a refresh — there is no other
	 *  "already submitted" flag, and there must not be one. */
	result?: ToolResultMessage;
}
export type ComponentRenderer = (props: ComponentProps) => React.ReactNode;

/** One built-in renderer per `render` kind a components.yaml entry can declare. */
const RENDERERS: Partial<Record<ProfileSummary["components"][number]["render"], ComponentRenderer>> = {
	table: Table,
	document: Document,
	checklist: Checklist,
	form: Form,
	tips: Tips,
	draft: Draft,
	schedule: Schedule,
	"page-editor": PageEditor,
	plan: Plan,
	answer: Answer,
	tree: Tree,
	files: Files,
	chart: Chart,
	research: Research,
};

/** Platform tools whose renderer is fixed by name, whatever the profile declares. */
const NATIVE: Record<string, ComponentRenderer> = {
	research_read: Research,
	research_propose: Research,
	research_execute_analysis: Research,
	research_finalize_report: Research,
};

/** A saved report shown as a document also carries its finalization badge. */
const REPORT_TOOL = "show_report";

/**
 * Maps a tool call's name back to the renderer it should use, via the profile's declared
 * components. Returns undefined both for a tool the profile never declared as a component (the
 * common case — every builtin/MCP/extension tool call) and for a declared `render` kind this
 * build has no renderer for yet — either way, the caller's existing collapsed tool-call panel is
 * exactly what such a call should keep getting. The research tools resolve by name first, with or
 * without components.
 */
export function lookupRenderer(toolName: string, components: ProfileSummary["components"] | undefined): ComponentRenderer | undefined {
	if (Object.hasOwn(NATIVE, toolName)) return NATIVE[toolName];
	const declared = components?.find((c) => c.name === toolName);
	if (!declared) return undefined;
	if (declared.render === "document" && toolName === REPORT_TOOL) return ReportDocument;
	return RENDERERS[declared.render];
}
